import { useEffect, useState } from "react";
import { X, Share, Plus, ChevronUp } from "lucide-react";

/**
 * iOS "Add to Home Screen" prompt — visible only:
 *   1. on iOS Safari (not Chrome/Firefox iOS, which can't install)
 *   2. when NOT already running in standalone mode (i.e. user hasn't installed yet)
 *   3. once per visitor (localStorage flag tracks dismissal — and a 14-day cooldown
 *      means if they dismiss and don't install, we ask again two weeks later)
 *   4. AFTER the user has spent ≥ 8 seconds on the page (avoids being intrusive
 *      on the first impression).
 *
 * Renders as a small "snackbar" docked above the BottomNav so it never blocks
 * primary navigation. Uses the same frosted-glass aesthetic as the rest of
 * the mobile shell.
 */
const STORAGE_KEY = "stage_studios_ios_install_prompt_v1";
const COOLDOWN_DAYS = 14;

interface StoredState {
  dismissedAt?: number;
  installed?: boolean;
}

// `navigator.standalone` is a non-standard iOS Safari property; `MSStream` is
// a legacy IE/Edge global used here only for UA exclusion. Both are typed via
// a local widening so TS doesn't complain about the missing lib definitions.
interface IosNavigator extends Navigator {
  standalone?: boolean;
}
interface IosWindow extends Window {
  MSStream?: unknown;
}

function shouldShowPrompt(): boolean {
  if (typeof window === "undefined") return false;

  // Dev/preview override — append `?install_prompt=preview` to any URL to
  // force the banner to render so designers and QA can see it on any
  // browser. Never gated by storage/UA when this flag is present.
  try {
    if (new URLSearchParams(window.location.search).get("install_prompt") === "preview") {
      return true;
    }
  } catch { /* URLSearchParams unsupported — fall through */ }

  // Already in standalone mode? Then the user installed — don't bug them.
  // iOS-specific (legacy) and modern PWA standalone checks.
  const standalone = (window.navigator as IosNavigator).standalone === true
    || window.matchMedia?.("(display-mode: standalone)").matches;
  if (standalone) return false;

  // iOS Safari detection. Chrome/Firefox on iOS use "CriOS"/"FxiOS" UA tokens
  // and cannot install web apps, so we hide the prompt for them.
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/.test(ua) && !(window as IosWindow).MSStream;
  const isSafari = !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  if (!isIOS || !isSafari) return false;

  // Cooldown check
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const { dismissedAt, installed }: StoredState = JSON.parse(raw);
      if (installed) return false;
      if (dismissedAt) {
        const ageMs = Date.now() - dismissedAt;
        if (ageMs < COOLDOWN_DAYS * 24 * 60 * 60 * 1000) return false;
      }
    }
  } catch { /* corrupt localStorage — fall through */ }

  return true;
}

export default function IosInstallPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!shouldShowPrompt()) return;
    // Preview override shows the prompt instantly; real iOS users get an
    // 8-second grace period so the prompt doesn't ambush their first impression.
    const isPreview = typeof window !== "undefined"
      && new URLSearchParams(window.location.search).get("install_prompt") === "preview";
    const t = setTimeout(() => setVisible(true), isPreview ? 200 : 8000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissedAt: Date.now() }));
    } catch { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Add Stage Studios to your home screen"
      className="md:hidden fixed left-3 right-3 z-50 bg-[var(--ink)] text-white shadow-2xl rounded-2xl overflow-hidden"
      style={{ bottom: "calc(var(--mobile-bottom-tab-height, 64px) + 12px)" }}
      data-testid="ios-install-prompt"
    >
      <div className="flex items-start gap-3 p-4">
        {/* App icon preview — same apple-touch-icon the user will pin */}
        <img
          src="/apple-touch-icon.png"
          alt=""
          className="w-11 h-11 rounded-[10px] shrink-0 mt-0.5"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="font-display text-base leading-tight tracking-tight">
            Get the Stage Studios app
          </div>
          <p className="text-[12px] text-white/70 mt-1 leading-snug">
            Add to your home screen for one-tap access and a full-screen, native experience.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="-mr-1 -mt-1 p-1.5 text-white/60 hover:text-white active:scale-90 transition-transform"
          aria-label="Dismiss install prompt"
          data-testid="ios-install-prompt-dismiss"
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>
      </div>

      {/* Step-by-step instructions — Safari's Share sheet flow */}
      <div className="border-t border-white/10 px-4 py-3 text-[12px] text-white/85 leading-relaxed">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="w-5 h-5 rounded-full bg-white/15 inline-flex items-center justify-center text-[10px] font-medium shrink-0">1</span>
          <span className="flex items-center gap-1.5">Tap <Share className="w-3.5 h-3.5 inline -mt-0.5" strokeWidth={1.6} /> in Safari&apos;s tab bar</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-white/15 inline-flex items-center justify-center text-[10px] font-medium shrink-0">2</span>
          <span className="flex items-center gap-1.5">Choose <Plus className="w-3.5 h-3.5 inline -mt-0.5" strokeWidth={1.6} /> &quot;Add to Home Screen&quot;</span>
        </div>
      </div>

      {/* Hint arrow pointing down at the Safari tab bar */}
      <div className="absolute -bottom-1 right-8 text-white/40">
        <ChevronUp className="w-4 h-4 rotate-180" strokeWidth={1.6} />
      </div>
    </div>
  );
}
